import { roundToPrecision } from '@cf-shared/shared-util';

/**
 * Calculate trailing average for a data series.
 * Used by cs-progress-chart when showAvg is enabled.
 *
 * @param data - Array of values (with undefined for missing data points)
 * @param precision - Number of decimal places for rounding
 * @param windowSize - Number of days to include in the average (default: 7)
 * @returns Array of averaged values
 */
export function calculateTrailingAverage(
  data: (number | undefined)[],
  precision: number,
  windowSize: number = 7
): (number | undefined)[] {
  // calculate trailing average (current day + previous days within window)
  return data.map((_, index) => {
    const startIdx = Math.max(0, index - windowSize + 1);
    const window = data.slice(startIdx, index + 1).filter(v => v !== undefined) as number[];
    if (window.length > 0) {
      const average = window.reduce((a, b) => a + b, 0) / window.length;
      return roundToPrecision(average, precision);
    }
    return undefined;
  });
}

/**
 * Calculate min/max range for a data series with padding.
 * Used by cs-progress-chart to set Y-axis bounds.
 *
 * @param data - Array of values (with undefined for missing data points)
 * @param paddingPercent - Percentage of range to add as padding (default: 0.1 = 10%)
 * @returns Object with min and max values
 */
export function calculateMinMax(
  data: (number | undefined)[],
  paddingPercent: number = 0.1
): { min: number; max: number } {
  const validValues = data.filter(v => v !== undefined) as number[];

  if (validValues.length === 0) {
    return { min: 0, max: 100 };
  }

  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  const range = max - min;

  // when the series is flat (all values equal — common on plateaus
  // and on stagnant exercise weight) the proportional padding rule
  // would collapse the Y axis to min === max, which Chart.js renders
  // as a degenerate scale with no ticks and an invisible line.
  // expand around the value instead: 10% of |value| with a small
  // absolute floor so an all-zero series also gets a visible band.
  if (range === 0) {
    const flatPadding = Math.max(Math.abs(min) * paddingPercent, 1);
    return {
      min: Math.max(0, min - flatPadding),
      max: max + flatPadding,
    };
  }

  const padding = range * paddingPercent;
  return {
    min: Math.max(0, min - padding),
    max: max + padding
  };
}

/**
 * Decide whether a chart series should render point markers.
 *
 * A Chart.js line dataset cannot draw a line for a single non-undefined
 * data point — and `cs-progress-chart` sets `pointRadius: 0` unless the
 * consumer opts in via `[showPoints]="true"`. So for a series with
 * exactly one valid point the lone value is invisible: no line, no
 * marker (#3704 — Bug B). Force a marker for that case so the value
 * still shows up.
 *
 * Multi-point series keep the existing "no markers unless asked"
 * behaviour to avoid cluttering dense lines.
 */
export function shouldShowMarker(
  values: (number | undefined)[],
  userShowPoints: boolean,
): boolean {
  if (userShowPoints) return true;
  let count = 0;
  for (const v of values) {
    if (v !== undefined) {
      count++;
      if (count > 1) return false;
    }
  }
  return count === 1;
}

/** The style reads the chart theme needs; a `CSSStyleDeclaration` satisfies it, and so does a test fake. */
export interface ChartStyleSource {
  getPropertyValue(name: string): string;
  fontSize?: string;
  fontFamily?: string;
}

/**
 * The chart theme tokens every chart shares, resolved once. Token lookups that depend on a
 * series or a direction (`s.colorVar`, `TREND_DIRECTION_COLOR_VARS`) are not here: they stay
 * with the chart that knows which name to read.
 */
export interface ChartTheme {
  grid: string;
  tick: string;
  pointBackground: string;
  pointRadius: number;
  pointHoverRadius: number;
  pointBorderWidth: number;
  tooltip: { bg: string; title: string; body: string; border: string };
  /** the host element's own font, so the tick labels match the text around the chart */
  font: { size: number; family: string };
}

/** fallbacks for when a token is missing, so chart.js never gets NaN or an empty font */
const DEFAULT_POINT_RADIUS = 4;
const DEFAULT_POINT_HOVER_RADIUS = 5;
const DEFAULT_POINT_BORDER_WIDTH = 2;
const DEFAULT_FONT_SIZE = 12;
const DEFAULT_FONT_FAMILY = 'system-ui, sans-serif';

/**
 * Build the chart theme from the document root's styles and, when given, the host element's
 * styles (for the font). Pure, so it is unit-tested with fake style objects.
 */
export function chartThemeFrom(rootStyles: ChartStyleSource, hostStyles?: ChartStyleSource): ChartTheme {
  const token = (name: string) => rootStyles.getPropertyValue(name).trim();
  const number = (name: string, fallback: number) => parseFloat(rootStyles.getPropertyValue(name)) || fallback;
  return {
    grid: token('--chart-grid'),
    tick: token('--chart-tick'),
    pointBackground: token('--chart-point-background'),
    pointRadius: number('--chart-point-radius', DEFAULT_POINT_RADIUS),
    pointHoverRadius: number('--chart-point-hover-radius', DEFAULT_POINT_HOVER_RADIUS),
    pointBorderWidth: number('--chart-point-border-width', DEFAULT_POINT_BORDER_WIDTH),
    tooltip: {
      bg: token('--chart-tooltip-bg'),
      title: token('--chart-tooltip-title'),
      body: token('--chart-tooltip-body'),
      border: token('--chart-tooltip-border'),
    },
    font: {
      size: parseInt(hostStyles?.fontSize ?? '', 10) || DEFAULT_FONT_SIZE,
      family: hostStyles?.fontFamily || DEFAULT_FONT_FAMILY,
    },
  };
}

/** Read the chart theme as it is right now; pass the chart's host element to take its font. */
export function readChartTheme(host?: Element): ChartTheme {
  return chartThemeFrom(getComputedStyle(document.documentElement), host ? getComputedStyle(host) : undefined);
}
